import React, { useContext } from 'react'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useTypedSelector } from 'src/State/RootReducer'
import { ChartSettings } from 'src/Libs/chart-kit'
import { CustomLayout, RechartData } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { DateSettings, MappedChartType, SaveChartProps } from 'src/Libs/system-data-handler/types'
import MappedChart from '../components/MappedChart'
import { DASHBOARD_HOME_CONSUMPTION_CHART_ID, DASHBOARD_PRODUCTION_CONSUMPTION_CHART_ID } from 'src/Libs/qa/ids'

const PRESET_IDS: (number | string)[] = [0, 2, 3, 4, 5, 6, 7, 8]
const DEFAULT_CHART_ID = 0
const DIRECT_CONSUMPTION_CHART_ID = 4
const DIRECT_CONSUMPTION_PRODUCTION_CHART_ID = 7

interface Props {
  installationId?: number
  storageId?: string
  defaultAndCustomCharts: CustomLayout[]
  handleSaveCharts: (props: SaveChartProps) => void
  chartSettings: ChartSettings[]
  rechartData: RechartData
  dateSettings: DateSettings
}

interface UseMappedChartsHook {
  pieProductionChart?: MappedChartType
  pieConsumptionChart?: MappedChartType
  buildingChart?: MappedChartType
  directConsumptionChart?: MappedChartType
  directConsumptionProductionChart?: MappedChartType
  customMappedCharts: MappedChartType[]
}

/**
 * A custom hook to get mapped default and custom charts which can be directly rendered
 */
const useMappedCharts = ({
  installationId,
  storageId,
  defaultAndCustomCharts,
  handleSaveCharts,
  chartSettings,
  dateSettings,
  rechartData,
}: Props): UseMappedChartsHook => {
  const { from, setFrom, setTo, setView, isForecastEnabled, to, view } = dateSettings
  const { t } = useCustomTranslation()
  const userId = useTypedSelector(state => state.auth.userId)
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)
  const homeConsuptionTooltipText = isDynamicPowerPriceEnabled
    ? t('systemDataHandler.pieCharts.homeConsumption.info-three-sixty-active')
    : t('systemDataHandler.pieCharts.homeConsumption.info-three-sixty-disabled')
  const productionTooltipText = t('systemDataHandler.pieCharts.production.info')

  const filterEditedDefaultCharts = (chart: CustomLayout): boolean => {
    const isCustomNotPresetChart =
      !chart.isInitial &&
      Number(chart.installationId) === Number(installationId) &&
      Number(chart.storageSerialNumber) === Number(storageId) &&
      !defaultAndCustomCharts.find(chartToFind => String(chartToFind.id) === String(chart.id) && chartToFind.isInitial)
    return chart.isInitial || isCustomNotPresetChart
  }

  const mappedCharts: MappedChartType[] = installationId
    ? defaultAndCustomCharts
        .filter(chart => filterEditedDefaultCharts(chart))
        .map((customLayout, idx) => {
          let dataQa = undefined

          if (customLayout.id === DIRECT_CONSUMPTION_CHART_ID) {
            dataQa = DASHBOARD_HOME_CONSUMPTION_CHART_ID
          } else if (customLayout.id === DIRECT_CONSUMPTION_PRODUCTION_CHART_ID) {
            dataQa = DASHBOARD_PRODUCTION_CONSUMPTION_CHART_ID
          }

          return {
            id: customLayout.id ?? idx,
            node: (
              <MappedChart
                chartSettingsList={chartSettings}
                chartProps={{
                  customLayout,
                  from,
                  rechartData,
                  setFrom,
                  setTo,
                  setView,
                  to,
                  view,
                  serialno: storageId,
                  pieChartTooltipText: customLayout.title === 'pieConsumptionHouse' ? homeConsuptionTooltipText : productionTooltipText,
                }}
                idx={idx}
                showForecast={isForecastEnabled}
                key={customLayout.id ?? idx}
                userId={userId}
                installationId={installationId}
                handleSaveChart={props => handleSaveCharts({ customLayout, chartSettings: props })}
                dataQa={dataQa}
              />
            ),
          }
        })
    : []

  const buildingChart = mappedCharts.find(chart => chart.id === DEFAULT_CHART_ID)
  const directConsumptionChart = mappedCharts.find(chart => chart.id === DIRECT_CONSUMPTION_CHART_ID)
  const directConsumptionProductionChart = mappedCharts.find(chart => chart.id === DIRECT_CONSUMPTION_PRODUCTION_CHART_ID)
  const customMappedCharts = mappedCharts.filter(chart => chart.id === undefined || !PRESET_IDS.includes(chart.id))

  return {
    buildingChart,
    directConsumptionChart,
    directConsumptionProductionChart,
    customMappedCharts,
  }
}

export default useMappedCharts
